import React, { useState, useMemo } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, StatusBadge, ConfirmDialog } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, KeyRound, Copy, Pencil, Trash2, Search, Check, Users } from "lucide-react";

const STATUS = ["active", "suspended", "disabled"];
const empty = { name: "", phone: "", email: "", password: "", status: "active", player_ids: [] };

export default function AdminParents() {
  const { data: parents, loading, refetch } = useFetch("/parents", []);
  const { data: players } = useFetch("/players", []);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);
  const [psearch, setPsearch] = useState("");
  const [creds, setCreds] = useState(null);
  const [del, setDel] = useState(null);

  const playerList = players || [];
  const filtered = useMemo(() => {
    const q = psearch.toLowerCase();
    return playerList.filter((p) => !q || p.full_name?.toLowerCase().includes(q) || p.player_code?.toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q) || (p.dob || "").includes(q));
  }, [playerList, psearch]);

  const openCreate = () => { setEdit(null); setF(empty); setCreds(null); setPsearch(""); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setF({ name: p.name, phone: p.phone || "", email: p.email, password: "", status: p.status || "active", player_ids: p.player_ids || [] }); setCreds(null); setPsearch(""); setOpen(true); };

  const togglePlayer = (id) => setF((s) => ({ ...s, player_ids: s.player_ids.includes(id) ? s.player_ids.filter((x) => x !== id) : [...s.player_ids, id] }));

  const genPassword = async () => {
    try {
      const { data } = await client.post("/parents/preview-credentials", { name: f.name });
      setF((s) => ({ ...s, password: data.password }));
      if (!edit) toast.success("Temporary password generated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Parent full name is required");
    try {
      if (edit) {
        const { data } = await client.put(`/parents/${edit.id}`, { name: f.name, phone: f.phone, status: f.status, player_ids: f.player_ids, ...(f.password ? { password: f.password } : {}) });
        if (data.credentials) setCreds(data.credentials); else setOpen(false);
        toast.success("Parent updated");
      } else {
        const { data } = await client.post("/parents", f);
        setCreds(data.credentials);
        toast.success("Parent account created");
      }
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => { try { await client.delete(`/parents/${del.id}`); toast.success("Parent deleted"); setDel(null); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  return (
    <div>
      <PageHeader title="Parents" subtitle="Create and manage parent portal accounts" testid="parents-page">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openCreate} data-testid="parent-create-btn"><UserPlus size={16} className="me-1" /> Create Parent Account</Button>
      </PageHeader>

      {loading ? <Loading /> : (parents || []).length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">No parent accounts yet. Create one to give a family portal access.</Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {parents.map((p) => (
            <Card key={p.id} className="p-5" data-testid={`parent-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-sm text-muted-foreground truncate">{p.email}</div>
                  {p.phone && <div className="text-xs text-muted-foreground mt-0.5">{p.phone}</div>}
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(p.players || []).map((c) => <span key={c.id} className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700">{c.full_name} · {c.category}</span>)}
                {(p.players || []).length === 0 && <span className="text-xs text-muted-foreground">No linked players</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`parent-edit-${p.id}`}><Pencil size={14} className="me-1" /> Edit</Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setDel(p)} data-testid={`parent-delete-${p.id}`}><Trash2 size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setCreds(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? "Edit Parent Account" : "Create Parent Account"}</DialogTitle></DialogHeader>

          {creds ? (
            <div className="space-y-4">
              <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-5">
                <p className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2"><Check size={16} /> Account ready — share these credentials with the parent</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2 bg-background rounded-lg px-3 py-2 border border-border">
                    <div className="min-w-0"><div className="text-xs text-muted-foreground">Academy Email</div><div className="font-mono text-sm truncate" data-testid="cred-email">{creds.email}</div></div>
                    <Button size="icon" variant="ghost" onClick={() => copy(creds.email)}><Copy size={15} /></Button>
                  </div>
                  <div className="flex items-center justify-between gap-2 bg-background rounded-lg px-3 py-2 border border-border">
                    <div className="min-w-0"><div className="text-xs text-muted-foreground">Temporary Password</div><div className="font-mono text-sm truncate" data-testid="cred-password">{creds.password}</div></div>
                    <Button size="icon" variant="ghost" onClick={() => copy(creds.password)}><Copy size={15} /></Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">The parent will be asked to change this password after first login at <b>/parent/login</b>.</p>
              </div>
              <DialogFooter><Button onClick={() => { setOpen(false); setCreds(null); }} data-testid="parent-done">Done</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Parent Full Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="parent-name" placeholder="Mohamed Touzani" /></div>
                <div><Label className="mb-1.5 block">Phone Number</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="parent-phone" placeholder="06 00 00 00 00" /></div>
              </div>
              {!edit && (
                <div>
                  <Label className="mb-1.5 block">Academy Email (username only — domain is fixed)</Label>
                  <div className="flex items-center">
                    <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value.replace(/@.*/, "") })} data-testid="parent-email" placeholder="auto-generated (e.g. parent.mohamed)" className="rounded-e-none" />
                    <span className="px-3 h-10 flex items-center rounded-e-md border border-s-0 border-input bg-muted text-sm text-muted-foreground whitespace-nowrap">@touzanielite.academy</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Leave blank to auto-generate a unique academy email from the parent name.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">{edit ? "Reset Password (optional)" : "Password"}</Label>
                  <div className="flex gap-2">
                    <Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="parent-password" placeholder={edit ? "leave blank to keep" : "auto or type one"} />
                    <Button type="button" variant="outline" onClick={genPassword} data-testid="parent-genpass"><KeyRound size={15} /></Button>
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Account Status</Label>
                  <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                    <SelectTrigger data-testid="parent-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block flex items-center gap-2"><Users size={15} /> Linked Players ({f.player_ids.length})</Label>
                <div className="relative mb-2"><Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} /><Input value={psearch} onChange={(e) => setPsearch(e.target.value)} placeholder="Search by name, ID, category or DOB" className="ps-9" data-testid="parent-player-search" /></div>
                <div className="border border-border rounded-lg divide-y divide-border max-h-52 overflow-y-auto scrollbar-thin">
                  {filtered.length === 0 && <div className="p-4 text-sm text-muted-foreground">No players found</div>}
                  {filtered.map((p) => {
                    const on = f.player_ids.includes(p.id);
                    return (
                      <button key={p.id} type="button" onClick={() => togglePlayer(p.id)} data-testid={`link-player-${p.id}`}
                        className={`w-full text-start flex items-center justify-between px-3 py-2 text-sm ${on ? "bg-emerald-500/10" : "hover:bg-muted"}`}>
                        <span className="font-medium">{p.full_name}</span>
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">{p.player_code} · {p.category} · {p.dob}{on && <Check size={15} className="text-emerald-600" />}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="parent-save">{edit ? "Save Changes" : "Create Account"}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Delete parent account?" description={`Permanently remove ${del?.name}? This cannot be undone.`} confirmText="Delete permanently" onConfirm={doDelete} />
    </div>
  );
}
